/**
 * A client's removed dashboards or reports (AgencyAnalytics' "Removed"
 * filter), folded away under a "Removed (n)" toggle, each with Restore.
 * A restored document comes back unshared.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { reportsApi, type RemovedDocument } from './reports-api.ts';

const removedOn = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium' });

export function RemovedDocuments({
  organisationId,
  clientId,
  show,
  refreshKey,
  onRestored,
}: {
  organisationId: string;
  clientId: string;
  show: 'dashboards' | 'reports';
  /** Changes whenever the live list changes, so a fresh removal shows up. */
  refreshKey: unknown;
  onRestored: () => void;
}): ReactNode {
  const [items, setItems] = useState<RemovedDocument[]>([]);
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(
    () =>
      reportsApi.removed(organisationId, clientId).then(
        (documents) =>
          setItems(
            documents.filter((item) => (item.kind === 'DASHBOARD') === (show === 'dashboards')),
          ),
        () => setItems([]),
      ),
    [organisationId, clientId, show],
  );
  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (items.length === 0) return null;
  return (
    <section className="rmv" aria-label={`Removed ${show}`}>
      <button
        type="button"
        className="linklike rmv-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? '▾' : '▸'} Removed ({items.length})
      </button>
      {problem && (
        <p className="rh-access-msg rh-access-error" role="alert">
          {problem}
        </p>
      )}
      {open && (
        <ul className="rmv-list">
          {items.map((item) => (
            <li key={item.id}>
              <span>
                <strong>{item.name}</strong>
                <small>Removed {removedOn.format(new Date(item.deleted_at))}</small>
              </span>
              <button
                type="button"
                className="btn small secondary"
                onClick={() =>
                  reportsApi.restoreRemoved(organisationId, item.id).then(
                    () => {
                      setProblem(null);
                      void load();
                      onRestored();
                    },
                    () => setProblem(`“${item.name}” could not be restored. Try again.`),
                  )
                }
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
